import { defineStore } from 'pinia'
import type { AxiosResponse } from 'axios'
import { DEFAULT_PAGINATION } from '@/constants/pagination'
import { NONE_WORKFLOW_STAGE } from '@/constants/workflows'
import { client } from '@/services/client'
import { useSpace } from '@/store/modules/App/space'
import { FeatureLimitsKeys } from '@/types'
import type {
  Comment,
  CommentResponse,
  Workflow,
  WorkflowResponse,
  WorkflowStage,
  WorkflowStageChange,
  WorkflowStageChangeResponse,
  WorkflowStageResponse,
} from '@/types'
import { getFeatureLimit } from '@/utils/helpers/planFeatureLimitHelper'
import { UNLIMITED_FEATURE_LIMIT_VALUES } from '@/components/Base/BaseLimitsNotification/constants'
import type { WorkflowFormData } from '@/components/ContentManager/EditorPage/Workflows/helpers'

export type State = {
  workflows: Workflow[]
  workflowStages: WorkflowStage[]
  workflowStageChanges: WorkflowStageChange[]
  isWorkflowFormInvalid: boolean
  isWorkflowLimitsModalOpen: boolean
}

const state = (): State => ({
  workflows: [],
  workflowStages: [],
  workflowStageChanges: [],
  isWorkflowFormInvalid: false,
  isWorkflowLimitsModalOpen: false,
})

export const useWorkflows = defineStore('workflows', {
  state,
  getters: {
    getWorkflows: (state: State): Workflow[] => state.workflows,
    getWorkflowStages: (state: State): WorkflowStage[] => state.workflowStages,
    getWorkflowStageById:
      (state: State): ((id: number) => Partial<WorkflowStage>) =>
      (id: number): Partial<WorkflowStage> => {
        if (id === NONE_WORKFLOW_STAGE.id) {
          return NONE_WORKFLOW_STAGE
        }
        return state.workflowStages.find((stage) => stage.id === id) || {}
      },
    getWorkflowStageChanges: (state: State): WorkflowStageChange[] =>
      state.workflowStageChanges,
    getCurrentWorkflow: (state: State): Partial<Workflow> => {
      const workflowId = state.workflowStageChanges[0]?.workflow_id
      return state.workflows?.find(({ id }) => id === workflowId) ?? {}
    },
    getCurrentWorkflowStage(): WorkflowStage | undefined {
      const workflowStageId = this.workflowStageChanges[0]?.workflow_stage_id
      return this.getCurrentWorkflow?.workflow_stages?.find(
        ({ id }: WorkflowStage) => id === workflowStageId,
      )
    },
    customWorkflowsLimit: (): number => {
      const spaceLimit = getFeatureLimit(FeatureLimitsKeys.CustomWorkflows)
      return UNLIMITED_FEATURE_LIMIT_VALUES.includes(spaceLimit)
        ? Infinity
        : spaceLimit
    },
    canCreateCustomWorkflow(): boolean {
      return this.customWorkflowsLimit > this.workflows.length
    },
  },
  actions: {
    setWorkflows(workflows: Workflow[]): void {
      this.workflows = workflows
    },
    setIsWorkflowFormInvalid(isWorkflowFormInvalid: boolean): void {
      this.isWorkflowFormInvalid = isWorkflowFormInvalid
    },
    setWorkflowStages(stages: WorkflowStage[]): void {
      this.workflowStages = stages
    },
    setWorkflowStageChanges(stageChanges: WorkflowStageChange[]): void {
      this.workflowStageChanges = stageChanges
    },
    setIsWorkflowLimitsModalOpen(
      isCustomWorkflowsLimitModalOpen: boolean,
    ): void {
      this.isWorkflowLimitsModalOpen = isCustomWorkflowsLimitModalOpen
    },

    async loadWorkflows(spaceId: number | string): Promise<void> {
      const { data } = await client.get(
        `spaces/${spaceId}/workflows?include_stages=true`,
        {
          region: useSpace().region,
        },
      )
      const workflows = data.workflows.sort(
        (a: Workflow, b: Workflow) => Number(a.id) - Number(b.id),
      )
      this.setWorkflows(workflows)
    },

    async loadWorkflowById({
      spaceId,
      id,
    }: {
      spaceId: number | string
      id: number
    }): Promise<void> {
      const { data } = await client.get(
        `spaces/${spaceId}/workflows/${id}?include_stages=true`,
        {
          region: useSpace().region,
        },
      )

      data.workflow.workflow_stages = data.workflow.workflow_stages.map(
        (stage: WorkflowStage) => ({
          ...stage,
          _new: false,
          _destroy: false,
        }),
      )

      return data.workflow
    },

    async loadWorkflowsByContentType({
      spaceId,
      contentType,
      isFolder,
    }: {
      spaceId: number | string
      contentType: string
      isFolder?: boolean
    }): Promise<Workflow[]> {
      const paramType = isFolder
        ? 'is_default=true'
        : `by_content_type=${contentType}`
      const { data } = await client.get(
        `spaces/${spaceId}/workflows?${paramType}&include_stages=true`,
        {
          region: useSpace().region,
        },
      )

      return data.workflows
    },

    async createWorkflow({
      spaceId,
      workflow,
    }: {
      spaceId: number | string
      workflow: Workflow
    }): Promise<WorkflowResponse> {
      return client.post(
        `spaces/${spaceId}/workflows`,
        { workflow },
        {
          region: useSpace().region,
        },
      )
    },

    async updateWorkflow({
      spaceId,
      workflow,
    }: {
      spaceId: number | string
      workflow: Workflow
    }): Promise<WorkflowResponse> {
      return client.put(
        `spaces/${spaceId}/workflows/${workflow.id}`,
        { workflow },
        {
          region: useSpace().region,
        },
      )
    },

    async deleteWorkflow({
      spaceId,
      id,
    }: {
      spaceId: number | string
      id: string
    }): Promise<void> {
      return await client.delete(`spaces/${spaceId}/workflows/${id}`, {
        region: useSpace().region,
      })
    },

    async duplicateWorkflow({
      spaceId,
      id,
      workflow,
    }: {
      spaceId: number | string
      id: number
      workflow: Workflow
    }): Promise<AxiosResponse<WorkflowResponse>> {
      return await client.post(
        `spaces/${spaceId}/workflows/${id}/duplicate`,
        {
          workflow,
        },
        {
          region: useSpace().region,
        },
      )
    },

    async createWorkflowStage({
      spaceId,
      stage,
    }: {
      spaceId: number | string
      stage: WorkflowStage
    }): Promise<WorkflowStageResponse> {
      return await client.post(
        `spaces/${spaceId}/workflow_stages/`,
        {
          workflow_stage: stage,
        },
        {
          region: useSpace().region,
        },
      )
    },

    async updateWorkflowStage({
      spaceId,
      stage,
    }: {
      spaceId: number | string
      stage: WorkflowStage
    }): Promise<void> {
      await client.put(
        `spaces/${spaceId}/workflow_stages/${stage.id}`,
        {
          workflow_stage: stage,
        },
        {
          region: useSpace().region,
        },
      )
    },

    async loadWorkflowStages({
      spaceId,
      workflowId,
    }: {
      spaceId: number | string
      workflowId?: string
    }): Promise<void> {
      const query = workflowId ? `?in_workflow=${workflowId}` : ''

      const { data } = await client.get(
        `spaces/${spaceId}/workflow_stages${query}`,
        {
          region: useSpace().region,
        },
      )

      this.setWorkflowStages(data.workflow_stages)
    },

    async loadWorkflowStageChanges({
      spaceId,
      perPage = 5,
      storyId = false,
      page = 1,
    }: {
      spaceId: number | string
      perPage?: number
      storyId?: string | boolean | number
      page?: number
    }) {
      const storyParam = storyId ? `&with_story=${storyId}` : ''

      const response = await client.get(
        `/spaces/${spaceId}/workflow_stage_changes?per_page=${perPage}&page=${page}${storyParam}`,
        {
          region: useSpace().region,
        },
      )
      this.setWorkflowStageChanges(response.data.workflow_stage_changes)
      return response
    },

    async createWorkflowStageChange(
      params: WorkflowFormData,
    ): Promise<WorkflowStageChangeResponse> {
      const {
        spaceId,
        storyId,
        workflowStageId,
        message = '',
        notify = false,
        releaseId = '0',
        spaceRoleIds = [],
        userIds = [],
        dueDate = null,
      } = params

      const payload = {
        workflow_stage_change: {
          story_id: storyId,
          workflow_stage_id: workflowStageId,
          due_date: dueDate,
        },
        comment: { message },
        assign: {
          space_role_ids: spaceRoleIds,
          user_ids: userIds,
        },
        notify,
        release_id: releaseId,
      }
      return client.post(`/spaces/${spaceId}/workflow_stage_changes`, payload, {
        region: useSpace().region,
      })
    },

    async deleteWorkflowStage({
      spaceId,
      stageId,
    }: {
      spaceId: number | string
      stageId: number
    }): Promise<void> {
      await client.delete(`/spaces/${spaceId}/workflow_stages/${stageId}`, {
        region: useSpace().region,
      })
    },

    async loadWorkflowComments(params: {
      storyId: string
      spaceId: number | string
      perPage: number
      page: number
    }): Promise<{ total: string; comments: Comment }> {
      const {
        storyId,
        spaceId,
        perPage = 10,
        page = DEFAULT_PAGINATION.currentPage,
      } = params
      const { data, headers } = await client.get(
        `/spaces/${spaceId}/comments?with_story=${storyId}&per_page=${perPage}&page=${page}`,
        {
          region: useSpace().region,
        },
      )

      const total = headers.total
      const comments = data.comments

      return { total, comments }
    },

    async createWorkflowComment({
      spaceId,
      storyId,
      releaseId,
      message,
    }: {
      spaceId: number | string
      storyId: string
      releaseId: string
      message: string
    }): Promise<CommentResponse> {
      return await client.post(
        `/spaces/${spaceId}/comments`,
        {
          comment: {
            message,
            release_id: releaseId,
            story_id: storyId,
          },
        },
        {
          region: useSpace().region,
        },
      )
    },

    async updateWorkflowComment({
      commentId,
      spaceId,
      message,
    }: {
      commentId: string
      spaceId: number | string
      message: string
    }): Promise<CommentResponse> {
      return await client.put(
        `/spaces/${spaceId}/comments/${commentId}`,
        {
          comment: {
            message,
          },
        },
        {
          region: useSpace().region,
        },
      )
    },

    async deleteWorkflowComment({
      commentId,
      spaceId,
    }: {
      commentId: string
      spaceId: number | string
    }): Promise<void> {
      const { data } = await client.delete(
        `/spaces/${spaceId}/comments/${commentId}`,
        {
          region: useSpace().region,
        },
      )
      return data
    },
  },
})
